// Merchant payout ledger — HTG owed and paid, via MonCash. A payout becomes
// eligible the moment a recipient confirms delivery; admin batches the
// disbursement.
//
// On DataTable, per Web Portal v2 (TABLES.m_payouts). Read-only: the merchant
// cannot disburse, and nothing here should imply they can.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PortalShell } from '../../components/shared/PortalShell';
import { StatusPill } from '../../components/shared/primitives';
import { DataTable, type Column } from '../../components/shared/DataTable';
import { MERCHANT_NAV } from '../_navs';
import { useMerchantNav } from '../../lib/pendingAction';
import { payouts, type Payout } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const METHOD_LABEL: Record<string, string> = {
  moncash: 'MonCash', natcash: 'NatCash', manual_bank: 'Bank transfer', cash: 'Cash pickup', other: 'Other',
};

export function MerchantPayoutsPage() {
  const q = useQuery({ queryKey: ['merchant', 'payouts'], queryFn: payouts.mine });
  const d = q.data;

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useMerchantNav(MERCHANT_NAV);

  const rows = useMemo(() => d?.payouts ?? [], [d]);

  const summary = useMemo(() => {
    if (!d) return undefined;

    // Held money is the merchant waiting on us to resolve a dispute. Kept
    // separate from "available" on purpose — folding the two together is how
    // someone ends up expecting a payment that is not coming.
    const held = rows
      .filter((p) => p.status === 'held')
      .reduce((a, p) => a + p.amount_htg_minor, 0);

    return [
      {
        label: 'Available',
        value: formatHtgMinor(d.eligible_htg_minor),
        hint: 'Confirmed, awaiting disbursement',
      },
      {
        label: 'Held',
        value: held > 0 ? formatHtgMinor(held) : '—',
        hint: held > 0 ? 'Pending dispute review' : 'No open disputes',
      },
      {
        label: 'Paid to date',
        value: formatHtgMinor(d.paid_htg_minor),
        hint: d.method ? (METHOD_LABEL[d.method] ?? d.method) : 'Payout method not set',
      },
    ];
  }, [d, rows]);

  const columns: Column<Payout>[] = [
    {
      key: 'ref', header: 'Payout', type: 'mono', width: '110px',
      value: (p) => `PO-${String(p.id).padStart(4, '0')}`,
    },
    {
      key: 'order', header: 'Order', type: 'mono', width: '110px',
      value: (p) => (p.request_id ? `REQ-${String(p.request_id).padStart(4, '0')}` : '—'),
    },
    {
      key: 'method', header: 'Method', type: 'text', width: 'minmax(0,1.2fr)',
      value: (p) => METHOD_LABEL[p.method] ?? p.method,
    },
    {
      key: 'moncash', header: 'Reference', type: 'mono', width: 'minmax(0,1fr)',
      value: (p) => p.moncash_transaction_id ?? p.moncash_reference ?? '—',
    },
    {
      key: 'date', header: 'Date', type: 'muted', width: '100px',
      value: (p) => new Date(p.paid_at ?? p.created_at).toLocaleDateString(),
    },
    {
      key: 'amount', header: 'Amount', type: 'money', width: '120px',
      value: (p) => formatHtgMinor(p.amount_htg_minor),
    },
    {
      key: 'status', header: 'Status', type: 'node', width: '110px',
      value: (p) => <StatusPill status={p.status} />,
    },
  ];

  return (
    <PortalShell
      nav={nav}
      activeId="payouts"
      title="Payouts"
      subtitle="Paid after each recipient confirms delivery with their code."
    >
      <DataTable
        title="Your payouts"
        subtitle="Most recent first."
        summary={summary}
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        loading={q.isLoading}
        empty={
          <>
            A payout becomes eligible the moment a recipient confirms a delivery.{' '}
            <Link to="/merchant/orders" className="font-semibold text-brand-600 hover:underline">
              See active orders →
            </Link>
          </>
        }
        renderCard={(p) => (
          <>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[12px] text-[var(--color-body)]">
                PO-{String(p.id).padStart(4, '0')}
              </span>
              <StatusPill status={p.status} />
            </div>
            <p className="mt-1 text-[15px] font-extrabold tabular-nums text-[var(--color-ink)]">
              {formatHtgMinor(p.amount_htg_minor)}
            </p>
            <p className="text-[12px] text-[var(--color-muted)]">
              {METHOD_LABEL[p.method] ?? p.method}
              {' · '}
              {new Date(p.paid_at ?? p.created_at).toLocaleDateString()}
            </p>
          </>
        )}
      />
    </PortalShell>
  );
}
